import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Container,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControl, IconButton, InputLabel, MenuItem,
  Paper, Select, Stack, Tab, Table, TableBody, TableCell,
  TableHead, TableRow, Tabs, TextField, Toolbar, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PublishIcon from '@mui/icons-material/Publish';
import Navigation from '../../Components/Navigation/navigation';
import { useAuth } from '../../Contexts/AuthContext';
import * as helpApi from '../../Helpers/helpApi';

const CATEGORIES = [
  'getting-started', 'excel-import', 'bridge-columns', 'calculated-accounts',
  'pro-forma', 'variance-analysis', 'stat-drivers', 'data-export', 'account-settings',
];

// --- Articles Tab ---
function ArticlesTab({ token }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ slug: '', title: '', category: '', summary: '', body: {} });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = () => {
    setLoading(true);
    // Use admin endpoint to get all articles (including drafts) — fall back to public list
    helpApi.getArticles(token).then(r => setArticles(r?.data || [])).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [token]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      if (editing) {
        await helpApi.adminUpdateArticle(editing.id, form, token);
      } else {
        await helpApi.adminCreateArticle(form, token);
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      setError('Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (id) => {
    await helpApi.adminPublishArticle(id, token);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    await helpApi.adminDeleteArticle(id, token);
    load();
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ slug: '', title: '', category: '', summary: '', body: {} });
    setDialogOpen(true);
  };

  const openEdit = (article) => {
    setEditing(article);
    setForm({ slug: article.slug, title: article.title, category: article.category, summary: article.summary || '', body: article.body || {} });
    setDialogOpen(true);
  };

  if (loading) return <CircularProgress sx={{ m: 3 }} />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button startIcon={<AddIcon />} variant="contained" onClick={openCreate}>New Article</Button>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Title</TableCell>
            <TableCell>Category</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {articles.map(a => (
            <TableRow key={a.id}>
              <TableCell>{a.title}</TableCell>
              <TableCell>{a.category}</TableCell>
              <TableCell><Chip label={a.status || 'published'} size="small" color={a.status === 'draft' ? 'default' : 'success'} /></TableCell>
              <TableCell>
                <IconButton size="small" onClick={() => openEdit(a)}><EditIcon fontSize="small" /></IconButton>
                {a.status === 'draft' && <IconButton size="small" onClick={() => handlePublish(a.id)}><PublishIcon fontSize="small" /></IconButton>}
                <IconButton size="small" color="error" onClick={() => handleDelete(a.id)}><DeleteIcon fontSize="small" /></IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Article' : 'New Article'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Slug" value={form.slug} onChange={e => setForm(p => ({ ...p, slug: e.target.value }))} size="small" fullWidth />
            <TextField label="Title" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} size="small" fullWidth />
            <FormControl size="small" fullWidth>
              <InputLabel>Category</InputLabel>
              <Select value={form.category} label="Category" onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Summary" value={form.summary} onChange={e => setForm(p => ({ ...p, summary: e.target.value }))} size="small" fullWidth multiline rows={2} />
            <TextField label="Body (JSON)" value={JSON.stringify(form.body, null, 2)} onChange={e => { try { setForm(p => ({ ...p, body: JSON.parse(e.target.value) })); } catch {} }} size="small" fullWidth multiline rows={6} helperText="Tiptap JSON document" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// --- FAQ Tab ---
function FaqTab({ token }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ question: '', answer: '', category: '', sort_order: 0 });
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    helpApi.getFaq(token).then(r => {
      const grouped = r?.data || {};
      const flat = Object.values(grouped).flat();
      setEntries(flat);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) {
        await helpApi.adminUpdateFaq(editing.id, form, token);
      } else {
        await helpApi.adminCreateFaq(form, token);
      }
      setDialogOpen(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this FAQ entry?')) return;
    await helpApi.adminDeleteFaq(id, token);
    load();
  };

  if (loading) return <CircularProgress sx={{ m: 3 }} />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => { setEditing(null); setForm({ question: '', answer: '', category: '', sort_order: 0 }); setDialogOpen(true); }}>New FAQ Entry</Button>
      </Box>
      <Table size="small">
        <TableHead><TableRow><TableCell>Question</TableCell><TableCell>Category</TableCell><TableCell>Actions</TableCell></TableRow></TableHead>
        <TableBody>
          {entries.map(e => (
            <TableRow key={e.id}>
              <TableCell sx={{ maxWidth: 300 }}>{e.question}</TableCell>
              <TableCell>{e.category}</TableCell>
              <TableCell>
                <IconButton size="small" onClick={() => { setEditing(e); setForm({ question: e.question, answer: e.answer, category: e.category, sort_order: e.sort_order || 0 }); setDialogOpen(true); }}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" color="error" onClick={() => handleDelete(e.id)}><DeleteIcon fontSize="small" /></IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit FAQ Entry' : 'New FAQ Entry'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Question" value={form.question} onChange={e => setForm(p => ({ ...p, question: e.target.value }))} size="small" fullWidth />
            <TextField label="Answer" value={form.answer} onChange={e => setForm(p => ({ ...p, answer: e.target.value }))} size="small" fullWidth multiline rows={4} />
            <FormControl size="small" fullWidth>
              <InputLabel>Category</InputLabel>
              <Select value={form.category} label="Category" onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Sort Order" type="number" value={form.sort_order} onChange={e => setForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} size="small" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// --- Videos Tab ---
function VideosTab({ token }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', category: '', embed_url: '', duration_seconds: '' });
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    helpApi.getVideos(token).then(r => setVideos(r?.data || [])).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = { ...form, duration_seconds: parseInt(form.duration_seconds) || 0 };
      if (editing) {
        await helpApi.adminUpdateVideo(editing.id, data, token);
      } else {
        await helpApi.adminCreateVideo(data, token);
      }
      setDialogOpen(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this video?')) return;
    await helpApi.adminDeleteVideo(id, token);
    load();
  };

  if (loading) return <CircularProgress sx={{ m: 3 }} />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => { setEditing(null); setForm({ title: '', category: '', embed_url: '', duration_seconds: '' }); setDialogOpen(true); }}>New Video</Button>
      </Box>
      <Table size="small">
        <TableHead><TableRow><TableCell>Title</TableCell><TableCell>Category</TableCell><TableCell>Duration</TableCell><TableCell>Actions</TableCell></TableRow></TableHead>
        <TableBody>
          {videos.map(v => (
            <TableRow key={v.id}>
              <TableCell>{v.title}</TableCell>
              <TableCell>{v.category}</TableCell>
              <TableCell>{v.duration_seconds}s</TableCell>
              <TableCell>
                <IconButton size="small" onClick={() => { setEditing(v); setForm({ title: v.title, category: v.category, embed_url: v.embed_url, duration_seconds: String(v.duration_seconds) }); setDialogOpen(true); }}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" color="error" onClick={() => handleDelete(v.id)}><DeleteIcon fontSize="small" /></IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Video' : 'New Video'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Title" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} size="small" fullWidth />
            <FormControl size="small" fullWidth>
              <InputLabel>Category</InputLabel>
              <Select value={form.category} label="Category" onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Embed URL" value={form.embed_url} onChange={e => setForm(p => ({ ...p, embed_url: e.target.value }))} size="small" fullWidth helperText="Vimeo, Loom, Synthesia, or any iframe-embeddable URL" />
            <TextField label="Duration (seconds)" type="number" value={form.duration_seconds} onChange={e => setForm(p => ({ ...p, duration_seconds: e.target.value }))} size="small" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// --- Release Notes Tab ---
function ReleaseNotesTab({ token }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ version: '', release_date: '', changes: { new_features: [], improvements: [], bug_fixes: [] } });
  const [saving, setSaving] = useState(false);
  const [changesText, setChangesText] = useState('{}');

  const load = () => {
    setLoading(true);
    helpApi.getReleaseNotes(token).then(r => setNotes(r?.data || [])).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      let changes = {};
      try { changes = JSON.parse(changesText); } catch {}
      const data = { ...form, changes };
      if (editing) {
        await helpApi.adminUpdateReleaseNote(editing.id, data, token);
      } else {
        await helpApi.adminCreateReleaseNote(data, token);
      }
      setDialogOpen(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <CircularProgress sx={{ m: 3 }} />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => {
          setEditing(null);
          setForm({ version: '', release_date: '', changes: {} });
          setChangesText(JSON.stringify({ new_features: [], improvements: [], bug_fixes: [] }, null, 2));
          setDialogOpen(true);
        }}>New Release Note</Button>
      </Box>
      <Table size="small">
        <TableHead><TableRow><TableCell>Version</TableCell><TableCell>Date</TableCell><TableCell>Status</TableCell><TableCell>Actions</TableCell></TableRow></TableHead>
        <TableBody>
          {notes.map(n => (
            <TableRow key={n.id}>
              <TableCell>v{n.version}</TableCell>
              <TableCell>{n.release_date}</TableCell>
              <TableCell><Chip label={n.status || 'published'} size="small" color={n.status === 'draft' ? 'default' : 'success'} /></TableCell>
              <TableCell>
                <IconButton size="small" onClick={() => {
                  setEditing(n);
                  setForm({ version: n.version, release_date: n.release_date, changes: n.changes || {} });
                  setChangesText(JSON.stringify(n.changes || {}, null, 2));
                  setDialogOpen(true);
                }}><EditIcon fontSize="small" /></IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Release Note' : 'New Release Note'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Version (e.g. 2.4.0)" value={form.version} onChange={e => setForm(p => ({ ...p, version: e.target.value }))} size="small" fullWidth />
            <TextField label="Release Date (YYYY-MM-DD)" value={form.release_date} onChange={e => setForm(p => ({ ...p, release_date: e.target.value }))} size="small" fullWidth />
            <TextField
              label="Changes (JSON)"
              value={changesText}
              onChange={e => setChangesText(e.target.value)}
              size="small"
              fullWidth
              multiline
              rows={8}
              helperText='{"new_features": [], "improvements": [], "bug_fixes": []}'
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// --- Feedback Summary Tab ---
function FeedbackSummaryTab({ token }) {
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    helpApi.adminGetFeedbackSummary(token).then(r => setSummary(r?.data || [])).finally(() => setLoading(false));
  }, [token]);

  if (loading) return <CircularProgress sx={{ m: 3 }} />;

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Content ID</TableCell>
          <TableCell>Type</TableCell>
          <TableCell>👍 Helpful</TableCell>
          <TableCell>👎 Not Helpful</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {summary.map((row, idx) => (
          <TableRow key={idx}>
            <TableCell>{row.content_id}</TableCell>
            <TableCell><Chip label={row.content_type} size="small" /></TableCell>
            <TableCell>{row.thumbs_up_count}</TableCell>
            <TableCell>{row.thumbs_down_count}</TableCell>
          </TableRow>
        ))}
        {summary.length === 0 && (
          <TableRow><TableCell colSpan={4}><Typography variant="body2" color="text.secondary">No feedback yet.</Typography></TableCell></TableRow>
        )}
      </TableBody>
    </Table>
  );
}

// --- Main Admin Page ---
export default function AdminKnowledgebase() {
  const auth = useAuth();
  const [tab, setTab] = useState(0);
  const token = auth.activeUser?.accessToken;
  const userType = auth.activeUser?.userType;

  // Only Super Users (userType 0) can access
  if (userType !== 0) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">You do not have permission to access this page.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Navigation title="Knowledgebase Admin" />
      <Box component="main" sx={{ flexGrow: 1, height: '100vh', overflow: 'auto', bgcolor: 'grey.100' }}>
        <Toolbar />
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>Knowledgebase Admin</Typography>
            <Divider sx={{ mb: 2 }} />
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
              <Tab label="Articles" />
              <Tab label="FAQ" />
              <Tab label="Videos" />
              <Tab label="Release Notes" />
              <Tab label="Feedback" />
            </Tabs>
            {tab === 0 && <ArticlesTab token={token} />}
            {tab === 1 && <FaqTab token={token} />}
            {tab === 2 && <VideosTab token={token} />}
            {tab === 3 && <ReleaseNotesTab token={token} />}
            {tab === 4 && <FeedbackSummaryTab token={token} />}
          </Paper>
        </Container>
      </Box>
    </Box>
  );
}
